import React, { useState } from "react"
import { Modal, ModalHeader, ModalBody, Button, Spinner } from "reactstrap"
import "./DocumentViewer.scss"

const DocumentViewer = ({
    isOpen,
    toggle,
    documentUrl,
    documentTitle = "Document",
    documentType = "image"
}) => {
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)
    const [corsRetry, setCorsRetry] = useState(false)

    // Reset states when opening
    React.useEffect(() => {
        if (isOpen) {
            setLoading(true)
            setError(false)
            setCorsRetry(false)
        }
    }, [isOpen, documentUrl, documentType, documentTitle])

    const handleImageLoad = () => {
        setLoading(false)
        setError(false)
    }

    const handleImageError = () => {
        if (!corsRetry) {
            // Try again without CORS
            setCorsRetry(true)
            setLoading(true)
            setError(false)
        } else {
            setLoading(false)
            setError(true)
        }
    }

    const handleDownload = () => {
        if (documentUrl) {
            const link = document.createElement('a')
            link.href = documentUrl
            link.download = documentTitle
            link.target = '_blank'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        }
    }

    const getFileExtension = (url) => {
        return url.split('.').pop().toLowerCase()
    }

    const getFileType = (url) => {
        const extension = getFileExtension(url)

        if (['pdf'].includes(extension)) return 'pdf'
        if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) return 'image'
        if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(extension)) return 'office'
        if (['txt', 'csv'].includes(extension)) return 'text'

        return 'unknown'
    }

    const renderDocumentContent = () => {
        if (!documentUrl || documentUrl === "N/A" || documentUrl === "") {
            return (
                <div className="document-error">
                    <i className="mdi mdi-file-document-outline"></i>
                    <p>No document available</p>

                </div>
            )
        }

        const fileType = getFileType(documentUrl)
        const fileExtension = getFileExtension(documentUrl)

        // Handle Office documents (Word, Excel, PowerPoint)
        if (fileType === 'office') {
            return (
                <div className="document-error">
                    <i className="mdi mdi-file-document-outline" style={{ fontSize: '4rem', color: '#0066cc' }}></i>
                    <h5 className="mt-3">Office Document (.{fileExtension})</h5>
                    <p>This is a {fileExtension.toUpperCase()} document that cannot be previewed in the browser.</p>
                    <div className="d-flex gap-2 justify-content-center mt-3">
                        <Button color="primary" onClick={handleDownload}>
                            <i className="mdi mdi-download me-1"></i>
                            Download Document
                        </Button>
                        <Button
                            color="outline-primary"
                            onClick={() => window.open(documentUrl, '_blank')}
                        >
                            <i className="mdi mdi-open-in-new me-1"></i>
                            Open in New Tab
                        </Button>
                    </div>
                    <div className="mt-3">
                        <small className="text-muted">
                            File: {documentUrl.split('/').pop()}
                        </small>
                    </div>
                </div>
            )
        }

        // Handle unknown file types
        if (fileType === 'unknown') {
            return (
                <div className="document-error">
                    <i className="mdi mdi-file-question-outline" style={{ fontSize: '4rem', color: '#ffc107' }}></i>
                    <h5 className="mt-3">Unknown File Type (.{fileExtension})</h5>
                    <p>This file type cannot be previewed in the browser.</p>
                    <div className="d-flex gap-2 justify-content-center mt-3">
                        <Button color="primary" onClick={handleDownload}>
                            <i className="mdi mdi-download me-1"></i>
                            Download File
                        </Button>
                        <Button
                            color="outline-primary"
                            onClick={() => window.open(documentUrl, '_blank')}
                        >
                            <i className="mdi mdi-open-in-new me-1"></i>
                            Open in New Tab
                        </Button>
                    </div>
                    <div className="mt-3">
                        <small className="text-muted">
                            File: {documentUrl.split('/').pop()}
                        </small>
                    </div>
                </div>
            )
        }

        // Check if it's a PDF
        if (fileType === 'pdf' || documentType === 'pdf') {
            return (
                <div className="pdf-viewer">
                    <iframe
                        src={documentUrl}
                        width="100%"
                        height="600px"
                        title={documentTitle}
                        onLoad={handleImageLoad}
                        onError={handleImageError}
                    />
                </div>
            )
        }

        // For images
        return (
            <div className="image-viewer">
                {loading && (
                    <div className="document-loading">
                        <Spinner color="primary" />
                        <p>Loading document...</p>
                    </div>
                )}
                {error && (
                    <div className="document-error">
                        <i className="mdi mdi-alert-circle-outline"></i>
                        <p>Failed to load document</p>
                        <small className="text-muted mb-2">
                            This might be due to CORS policy or invalid URL
                        </small>
                        <div className="d-flex gap-2 justify-content-center">
                            <Button color="primary" size="sm" onClick={handleDownload}>
                                <i className="mdi mdi-download me-1"></i>
                                Download Instead
                            </Button>
                            <Button
                                color="outline-primary"
                                size="sm"
                                onClick={() => window.open(documentUrl, '_blank')}
                            >
                                <i className="mdi mdi-open-in-new me-1"></i>
                                Open in New Tab
                            </Button>
                        </div>

                    </div>
                )}
                <img
                    src={documentUrl}
                    alt={documentTitle}
                    className={`document-image ${loading ? 'd-none' : ''}`}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    crossOrigin={corsRetry ? undefined : "anonymous"}
                    key={corsRetry ? 'no-cors' : 'with-cors'}
                />
            </div>
        )
    }

    return (
        <Modal
            isOpen={isOpen}
            toggle={toggle}
            size="lg"
            centered
            className="document-viewer-modal"
        >
            <ModalHeader toggle={toggle} className="d-flex justify-content-between align-items-center">
                <span>{documentTitle}</span>
                <div className="document-actions">
                    <Button
                        color="outline-primary"
                        size="sm"
                        onClick={handleDownload}
                        className="me-2"
                    >
                        <i className="mdi mdi-download me-1"></i>
                        Download
                    </Button>
                    <Button
                        color="outline-secondary"
                        size="sm"
                        onClick={() => window.open(documentUrl, '_blank')}
                    >
                        <i className="mdi mdi-open-in-new me-1"></i>
                        Open in New Tab
                    </Button>
                </div>
            </ModalHeader>
            <ModalBody className="p-0">
                {renderDocumentContent()}
            </ModalBody>
        </Modal>
    )
}

export default DocumentViewer